import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { ORDER_TYPE_ENUM, platformKey } from "@/constants/enums";
import { USER_ROLES_ENUM } from "@/constants/permissions";
import type { Shift, Staff } from "@/types";
import { evaluateShifts, groupShiftsByPlatform } from "@/utils/time";
import type { RootState } from "..";

/**
 * The all-staff slice — the original's module **`63998`** (`Fe` =
 * `fetchAllStaff`, `tl` = the `{ allStaff, currentShiftStaff }` selector).
 *
 * It exists for exactly one surface: `ASSIGN_ORDER_TAKER_MODAL`. The list is
 * fetched once per browsing session — the original guards on a
 * `sessionStorage` key (`CACHED_STAFF`) as well as on the list being non-empty,
 * so a modal reopened five times makes one request — and that guard is
 * reproduced, because the roster does not change during a shift.
 */

/** `SB.CACHED_STAFF` — the session key the fetch guard reads. */
export const CACHED_STAFF_KEY = "CACHED_STAFF";

export interface AllStaffState {
  data: Staff[];
  loading: boolean;
}

const initialState: AllStaffState = { data: [], loading: false };

const allStaffSlice = createSlice({
  name: "all-staff",
  initialState,
  reducers: {
    setAllStaff(state, action: PayloadAction<Staff[]>) {
      state.data = Array.isArray(action.payload) ? action.payload : [];
      state.loading = false;
    },
    setAllStaffLoading(state, action: PayloadAction<boolean>) {
      state.loading = Boolean(action.payload);
    },
    resetAllStaff() {
      return initialState;
    },
  },
});

export const { setAllStaff, setAllStaffLoading, resetAllStaff } =
  allStaffSlice.actions;
export default allStaffSlice.reducer;

/* -------------------------------------------------------------------------- */
/* `tl` — the two lists behind the modal's two tabs                           */
/* -------------------------------------------------------------------------- */

/** The three roles that may be an order taker. Nothing else appears at all. */
const ORDER_TAKER_ROLES: string[] = [
  USER_ROLES_ENUM.CASHIER,
  USER_ROLES_ENUM.STAFF,
  USER_ROLES_ENUM.SALES_PERSON,
];

/**
 * `false === findApplicableSchedule(staff.operational_hours, getPlatformKey(DINE_IN))`,
 * expressed against this tree's own shift machinery.
 *
 * A staff member is "on shift" when their personal `operational_hours` carry a
 * window covering right now for the dine-in platform. The original asks its
 * schedule resolver for the applicable schedule and treats `false` — no
 * schedule applies — as disabled; `evaluateShifts` answers the same question
 * with the same window semantics (including the overnight tail), so `"OPEN"`
 * is that resolver's truthy answer.
 *
 * **On this deployment every staff row carries `operational_hours: []`**, so
 * the Current Shift tab is empty for everyone and All Staff is the tab that
 * has rows. That is the data, not a defect — it is already recorded in
 * `RECOVERY_PLAN.md`, where the same emptiness was found from the POS side.
 */
const isOnShiftNow = (staff: Staff, now: Date = new Date()): boolean => {
  const hours = Array.isArray(staff.operational_hours)
    ? (staff.operational_hours as Shift[])
    : [];
  if (hours.length === 0) return false;
  const grouped = groupShiftsByPlatform(hours);
  const days = grouped[platformKey(ORDER_TYPE_ENUM.DINE_IN)];
  if (!days) return false;
  return evaluateShifts(days, now) === "OPEN";
};

const selectAllStaffData = (state: RootState) => state.allStaff.data;

/**
 * `_` / `tl`, verbatim in shape:
 *
 * ```js
 * e.forEach((e) => {
 *   if (!e.is_disabled && [CASHIER, STAFF, SALES_PERSON].includes(e.role)) {
 *     let n = !1 === findApplicableSchedule(e.operational_hours, zT(DINE_IN));
 *     (t.push({ ...e, is_disabled: n }), n || r.push({ ...e, is_disabled: n }));
 *   }
 * });
 * ```
 *
 * Note what `is_disabled` MEANS on the way out: it is overwritten with
 * "off-shift", not with the record's own disabled flag (a genuinely disabled
 * staff member never reaches this point). The modal renders the shift-clock
 * icon off it.
 */
export const selectOrderTakers = createSelector(
  [selectAllStaffData],
  (rows): { allStaff: Staff[]; currentShiftStaff: Staff[] } => {
    const allStaff: Staff[] = [];
    const currentShiftStaff: Staff[] = [];
    rows.forEach((staff) => {
      if (staff.is_disabled === true) return;
      if (!ORDER_TAKER_ROLES.includes(String(staff.role ?? ""))) return;
      const offShift = !isOnShiftNow(staff);
      const row = { ...staff, is_disabled: offShift };
      allStaff.push(row);
      if (!offShift) currentShiftStaff.push(row);
    });
    return { allStaff, currentShiftStaff };
  },
);
